import store_consultation from '@/store/mods/consultations/consultations'
import calendar from '@/utils/clinic-calendar'

// Rendez-vous par jour, par semaine (lundi → dimanche) ou par mois autour d'un jour d'ancrage (contrat C8 : periode + date),
// présentés comme à l'Accueil. Médecin : ses seuls rendez-vous, honoré / absent le jour venu, annulation motivée.
// Infirmier : tous les médecins (filtre médecin), lecture seule. Tous les états sont listés : la colonne État les distingue.
// Un clic sur Jour, Semaine ou Mois (même celui affiché) revient à la période d'aujourd'hui ; les flèches partent de là.
const PERIODES = [{ id: 'jour', nom: 'Jour', aide: 'Afficher aujourd\'hui' }, { id: 'semaine', nom: 'Semaine', aide: 'Afficher cette semaine' }, { id: 'mois', nom: 'Mois', aide: 'Afficher ce mois-ci' }]
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const FLECHES = { jour: ['Jour précédent', 'Jour suivant'], semaine: ['Semaine précédente', 'Semaine suivante'], mois: ['Mois précédent', 'Mois suivant'] }
const PAR_PAGE = 50          // une semaine chargée se lit d'un coup ; C8 accepte 1 à 100
const DELAI_SAISIE = 300     // recherche : une seule requête par saisie
let instances = 0

// Jours « AAAA-MM-JJ » calculés en UTC à midi : aucun décalage d'heure d'été ni de fuseau du navigateur
const deux = nombre => String(nombre).padStart(2, '0')
const date_utc = jour => new Date(jour + 'T12:00:00Z')
const vers_jour = date => String(date.getUTCFullYear()).padStart(4, '0') + '-' + deux(date.getUTCMonth() + 1) + '-' + deux(date.getUTCDate())
const jour_valide = valeur => {
    if (typeof valeur !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valeur)) return false
    const date = date_utc(valeur)
    return !isNaN(date.getTime()) && vers_jour(date) === valeur
}
// Les flèches restent entre 1900 et 2199
const jour_navigable = valeur => jour_valide(valeur) && Number(valeur.substr(0, 4)) >= 1900 && Number(valeur.substr(0, 4)) <= 2199
const decaler_jour = (jour, delta) => {
    const date = date_utc(jour)
    date.setUTCDate(date.getUTCDate() + delta)
    return vers_jour(date)
}
const lundi = jour => decaler_jour(jour, -((date_utc(jour).getUTCDay() + 6) % 7))
const premier_du_mois = jour => jour.substr(0, 8) + '01'
const decaler_mois = (jour, delta) => {
    const date = date_utc(premier_du_mois(jour))
    date.setUTCMonth(date.getUTCMonth() + delta)
    return vers_jour(date)
}
const bornes_periode = (periode, jour) => {
    if (periode === 'semaine') return [lundi(jour), decaler_jour(lundi(jour), 6)]
    if (periode === 'mois') return [premier_du_mois(jour), decaler_jour(decaler_mois(jour, 1), -1)]
    return [jour, jour]
}
const jj_mm = jour => jour.substr(8, 2) + '/' + jour.substr(5, 2)
const majuscule = texte => texte.charAt(0).toUpperCase() + texte.slice(1)
const message_serveur = (error, defaut) => error && error.response && error.response.data && error.response.data.message ? error.response.data.message : defaut

export default {
    name: 'rendez-vous-medecin',
    props: {
        mode_sage_femme: { type: Boolean, default: false },
        tableau_api: { type: Object, default: () => store_consultation },
        mode_infirmier: { type: Boolean, default: false },   // tous les médecins, lecture seule, colonne et filtre Médecin
        carte: { type: Boolean, default: false },            // carte du tableau de bord (titre, Actualiser) ; sinon intégré (onglet, fenêtre)
        titre: { type: String, default: 'Mes rendez-vous' },
        periode_initiale: { type: String, default: '' },     // jour, semaine ou mois ; par défaut semaine (médecin), jour (infirmier)
        date_initiale: { type: String, default: '' }         // jour d'ancrage AAAA-MM-JJ ; par défaut aujourd'hui (fuseau de la clinique)
    },
    data() {
        const periode = PERIODES.some(item => item.id === this.periode_initiale) ? this.periode_initiale : (this.mode_infirmier ? 'jour' : 'semaine')
        return {
            uid: 'rdv-medecin-' + (++instances),
            periodes: PERIODES,
            filtre: { periode, date: jour_valide(this.date_initiale) ? this.date_initiale : '', medecin_id: '', recherche: '', page: 1 },
            aujourdhui_clinique: '', fuseau_horaire: 'UTC', liste_medecins: [],
            liste: [], total: 0, pages: 1, page_liste: 1, is_loading: true, erreur: '', info: '', sequence: 0,
            recherche_timer: null, refresh_timer: null, est_detruit: false,
            en_action: [], annulation: null, motif: '', erreur_annulation: '', is_saving: false, ouverts: [],
            // Les indicateurs suivent exactement les bornes affichées (médecin connecté).
            indicateurs: null, indicateurs_periode: '', indicateurs_sequence: 0
        }
    },
    computed: {
        // Jour d'ancrage : celui demandé, sinon aujourd'hui (la Clinique renvoie son « aujourd'hui » dès la première réponse)
        ancre() {
            return jour_valide(this.filtre.date) ? this.filtre.date : this.aujourdhui_reference()
        },
        bornes() { return bornes_periode(this.filtre.periode, this.ancre) },
        // « Vendredi 2 octobre 2026 », « Semaine du 28/09 au 04/10/2026 », « Octobre 2026 »
        libelle_periode() {
            const du = this.bornes[0], au = this.bornes[1]
            if (this.filtre.periode === 'semaine') return 'Semaine du ' + jj_mm(du) + (du.substr(0, 4) === au.substr(0, 4) ? '' : '/' + du.substr(0, 4)) + ' au ' + jj_mm(au) + '/' + au.substr(0, 4)
            if (this.filtre.periode === 'mois') return majuscule(MOIS[Number(du.substr(5, 2)) - 1]) + ' ' + du.substr(0, 4)
            return this.libelle_date(du)
        },
        colonnes() { return this.mode_infirmier ? 8 : 7 },
        // Semaine et mois : lignes regroupées par jour (ordre du serveur) ; jour : un seul groupe, sans en-tête
        groupes() {
            if (this.filtre.periode === 'jour') return this.liste.length ? [{ jour: 'jour-unique', libelle: '', relatif: '', lignes: this.liste }] : []
            const groupes = [], index = {}
            this.liste.forEach(ligne => {
                const jour = this.jour_ligne(ligne) || 'sans-date'
                if (!index[jour]) {
                    index[jour] = { jour, libelle: jour === 'sans-date' ? 'Date non renseignée' : this.libelle_date(jour), relatif: this.relatif(jour), lignes: [] }
                    groupes.push(index[jour])
                }
                index[jour].lignes.push(ligne)
            })
            // Jour coupé par la pagination (premier groupe après la page 1, dernier avant la dernière page) : son nombre ne couvre que cette page
            groupes.forEach((groupe, position) => {
                const avant = position === 0 && this.page_liste > 1, apres = position === groupes.length - 1 && this.page_liste < this.pages
                groupe.complet = !avant && !apres
                groupe.aide = avant && apres ? 'Ce jour peut aussi figurer sur les pages précédente et suivante' : avant ? 'Ce jour peut commencer sur la page précédente' : apres ? 'Ce jour peut se poursuivre sur la page suivante' : ''
            })
            return groupes
        }
    },
    mounted() {
        this.charger()
        if (typeof window !== 'undefined') {
            window.addEventListener('focus', this.actualiser)
            window.addEventListener('clinicaa:consultation-updated', this.actualiser)
        }
        this.refresh_timer = setInterval(() => {
            if (typeof document === 'undefined' || !document.hidden) this.actualiser()
        }, 60000)
    },
    activated() { this.actualiser() },
    beforeDestroy() {
        this.est_detruit = true
        this.sequence++
        this.indicateurs_sequence++
        clearInterval(this.refresh_timer)
        clearTimeout(this.recherche_timer)
        this.refresh_timer = null
        this.recherche_timer = null
        if (typeof window !== 'undefined') {
            window.removeEventListener('focus', this.actualiser)
            window.removeEventListener('clinicaa:consultation-updated', this.actualiser)
        }
    },
    methods: {
        aujourdhui_reference() {
            if (jour_valide(this.aujourdhui_clinique)) return this.aujourdhui_clinique
            const jour = calendar.day(new Date(), this.fuseau_horaire)
            return jour_valide(jour) ? jour : vers_jour(new Date())
        },
        async acces() {
            const credential = await (this.mode_infirmier ? (this.tableau_api || store_consultation).getTableauInfirmierAccess() : (this.tableau_api || store_consultation).getTableauMedecinAccess())
            if (!credential || !credential.success || !credential.data || !credential.data.access_token) throw new Error('Accès indisponible')
            return credential
        },
        // Liste de la période affichée ; une réponse plus ancienne que la dernière demande est ignorée
        async charger(discret = false, forcer_indicateurs = discret) {
            if (this.est_detruit) return
            const sequence = ++this.sequence
            const filtre = Object.assign({}, this.filtre)
            if (!discret) this.is_loading = true
            this.erreur = ''
            try {
                const credential = await this.acces()
                if (this.est_detruit || sequence !== this.sequence) return
                if (this.mode_infirmier && Array.isArray(credential.data.medecins)) {
                    this.liste_medecins = credential.data.medecins.filter(medecin => Number(medecin.id) > 0)
                        .map(medecin => ({ id: medecin.id, name: medecin.nom || medecin.name || 'Médecin n° ' + medecin.id }))
                }
                const payload = { periode: filtre.periode, search_item: filtre.recherche, page: filtre.page, per_page: PAR_PAGE, access_token: credential.data.access_token }
                if (jour_valide(filtre.date)) payload.date = filtre.date
                if (this.mode_infirmier && filtre.medecin_id) payload.medecin_id = filtre.medecin_id
                const response = await (this.mode_infirmier ? (this.tableau_api || store_consultation).getRendezVousInfirmier(payload) : (this.tableau_api || store_consultation).getRendezVousMedecin(payload))
                if (this.est_detruit || sequence !== this.sequence) return
                if (!response || !Array.isArray(response.data)) throw new Error('Réponse inattendue')
                const derniere_page = Math.max(1, Number(response.last_page) || 1)
                // Page devenue vide (rendez-vous reprogrammés entre-temps) : retour à la dernière page existante
                if (!response.data.length && filtre.page > derniere_page) {
                    this.filtre.page = derniere_page
                    return this.charger(discret, forcer_indicateurs)
                }
                this.liste = response.data
                this.total = Number(response.total) || 0
                this.pages = derniere_page
                this.page_liste = filtre.page
                this.fuseau_horaire = calendar.timezone(response.timezone)
                this.aujourdhui_clinique = jour_valide(response.aujourdhui) ? response.aujourdhui : calendar.day(new Date(), this.fuseau_horaire)
                // Sans date demandée, la Clinique ancre la période sur son aujourd'hui
                if (!jour_valide(this.filtre.date) && jour_valide(response.date)) this.filtre.date = response.date
                if (!this.mode_infirmier && !this.mode_sage_femme) this.charger_indicateurs(forcer_indicateurs)
            } catch (error) {
                if (!this.est_detruit && sequence === this.sequence) {
                    this.erreur = 'Impossible de charger les rendez-vous. Vérifiez la connexion puis réessayez.'
                    this.liste = []
                }
            } finally {
                if (!this.est_detruit && sequence === this.sequence) this.is_loading = false
            }
        },
        // Bouton Actualiser et « Réessayer »
        recharger() {
            clearTimeout(this.recherche_timer)
            this.recherche_timer = null
            return this.charger(false, true)
        },
        // Retour sur la fenêtre, fin de consultation, minuterie : rechargement discret quand rien n'est en cours
        actualiser() {
            if (this.est_detruit || this.is_loading || this.recherche_timer) return Promise.resolve()
            return this.charger(true)
        },
        filtrer() {
            clearTimeout(this.recherche_timer)
            this.recherche_timer = null
            this.filtre.page = 1
            return this.charger()
        },
        chercher() {
            clearTimeout(this.recherche_timer)
            this.recherche_timer = setTimeout(() => {
                this.recherche_timer = null
                this.filtre.page = 1
                this.charger()
            }, DELAI_SAISIE)
        },
        // Jour / Semaine / Mois, même celui affiché : la période qui contient aujourd'hui. Sans date, la Clinique ancre sur son
        // aujourd'hui (fuseau de la clinique) et renvoie ce jour, comme au premier chargement.
        choisir_periode(periode) {
            if (!PERIODES.some(item => item.id === periode)) return Promise.resolve()
            this.filtre.periode = periode
            this.filtre.date = ''
            return this.filtrer()
        },
        // Flèches : un jour, une semaine ou un mois (le mois s'ancre sur son premier jour)
        deplacer(delta) {
            if (delta !== -1 && delta !== 1) return Promise.resolve()
            const cible = this.filtre.periode === 'mois' ? decaler_mois(this.ancre, delta) : decaler_jour(this.ancre, this.filtre.periode === 'semaine' ? 7 * delta : delta)
            if (!jour_navigable(cible)) return Promise.resolve()
            this.filtre.date = cible
            return this.filtrer()
        },
        libelle_fleche(delta) {
            return (FLECHES[this.filtre.periode] || FLECHES.jour)[delta < 0 ? 0 : 1]
        },
        changer_page(page) {
            if (this.is_loading || page < 1 || page > this.pages || page === this.filtre.page) return Promise.resolve()
            this.filtre.page = page
            return this.charger()
        },
        // Jour local du rendez-vous (clinique) : jour_local du serveur, sinon déduit de date_rdv
        jour_ligne(ligne) {
            if (jour_valide(ligne.jour_local)) return ligne.jour_local
            return ligne.date_rdv ? calendar.day(ligne.date_rdv, this.fuseau_horaire) : ''
        },
        a_fixer(ligne) { return ligne.heure_a_fixer === true || Number(ligne.heure_a_fixer) === 1 },
        // Heure affichée : jamais « 00:00 » pour un rendez-vous donné sans heure
        heure(ligne) {
            if (this.a_fixer(ligne)) return ''
            if (typeof ligne.heure_locale === 'string' && /^\d{2}:\d{2}$/.test(ligne.heure_locale)) return ligne.heure_locale
            const parts = calendar.parts(ligne.date_rdv, this.fuseau_horaire)
            return parts ? parts.hour + ':' + parts.minute : '—'
        },
        // « Lundi 28 septembre 2026 »
        libelle_date(jour) {
            if (!jour_valide(jour)) return ''
            const date = date_utc(jour)
            return majuscule(JOURS[date.getUTCDay()] + ' ' + date.getUTCDate() + ' ' + MOIS[date.getUTCMonth()] + ' ' + date.getUTCFullYear())
        },
        relatif(jour) {
            const aujourdhui = this.aujourdhui_reference()
            if (!jour_valide(jour) || !jour_valide(aujourdhui)) return ''
            if (jour === aujourdhui) return 'Aujourd\'hui'
            if (jour === decaler_jour(aujourdhui, 1)) return 'Demain'
            if (jour === decaler_jour(aujourdhui, -1)) return 'Hier'
            return ''
        },
        // « Vendredi 2 octobre 2026 à 09:30 » ou « Vendredi 2 octobre 2026 · heure à fixer par l'Accueil »
        date_complete(ligne) {
            const jour = this.libelle_date(this.jour_ligne(ligne))
            if (!jour) return '—'
            if (!this.a_fixer(ligne)) return jour + ' à ' + this.heure(ligne)
            return jour + (ligne.statut === 'planifie' ? ' · heure à fixer par l\'Accueil' : ' · sans heure')
        },
        message_vide() {
            if (String(this.filtre.recherche || '').trim()) return 'Aucun rendez-vous ne correspond à cette recherche.'
            const periode = this.filtre.periode === 'semaine' ? 'pour la s' + this.libelle_periode.slice(1)
                : this.filtre.periode === 'mois' ? 'en ' + this.libelle_periode.charAt(0).toLowerCase() + this.libelle_periode.slice(1)
                    : 'le ' + this.libelle_periode.charAt(0).toLowerCase() + this.libelle_periode.slice(1)
            return 'Aucun rendez-vous ' + periode + '.' + (this.mode_infirmier || this.mode_sage_femme ? '' : ' Ceux que vous donnez en consultation apparaissent ici.')
        },
        tone(statut) {
            return { planifie: 'primary', honore: 'success', absent: 'warning', annule: 'danger' }[statut] || 'muted'
        },
        occupe(ligne) { return this.en_action.indexOf(ligne.rendez_vous_id) !== -1 },
        est_ouvert(id) { return this.ouverts.indexOf(id) !== -1 },
        basculer(id) { this.ouverts = this.est_ouvert(id) ? this.ouverts.filter(item => item !== id) : this.ouverts.concat([id]) },
        // Honoré / absent : au plus tôt le jour du rendez-vous (clinique), sur ses rendez-vous planifiés (la Clinique le vérifie aussi)
        peut_marquer(ligne) {
            const jour = this.jour_ligne(ligne)
            return !this.mode_infirmier && !this.mode_sage_femme && ligne.statut === 'planifie' && !!jour && jour <= calendar.day(new Date(), this.fuseau_horaire)
        },
        async changer_statut(ligne, statut, motif) {
            if (this.mode_sage_femme) throw new Error('Agenda en lecture seule')
            const credential = await (this.tableau_api || store_consultation).getTableauMedecinAccess()
            if (!credential || !credential.success || !credential.data || !credential.data.access_token) throw new Error('Accès médecin indisponible')
            const payload = { rendez_vous_id: ligne.rendez_vous_id, statut, access_token: credential.data.access_token }
            if (motif) payload.motif = motif
            const response = await (this.tableau_api || store_consultation).changerStatutRendezVousMedecin(payload)
            if (!response || response.success !== true) throw new Error('Refus')
            return response
        },
        async marquer(ligne, statut) {
            if (this.occupe(ligne) || !this.peut_marquer(ligne)) return
            this.en_action = this.en_action.concat([ligne.rendez_vous_id])
            this.erreur = ''
            let refus = ''
            try {
                const response = await this.changer_statut(ligne, statut)
                if (this.est_detruit) return
                this.info = response.message || 'État enregistré.'
            } catch (error) {
                refus = message_serveur(error, 'L\'état n\'a pas été enregistré. Actualisez puis réessayez.')
            }
            if (this.est_detruit) return
            this.en_action = this.en_action.filter(id => id !== ligne.rendez_vous_id)
            await this.charger(true)
            if (refus && !this.erreur && !this.est_detruit) this.erreur = refus
        },
        ouvrir_annulation(ligne) {
            if (this.mode_sage_femme) return
            this.annulation = ligne
            this.motif = ''
            this.erreur_annulation = ''
        },
        async confirmer_annulation() {
            if (this.is_saving || !this.annulation) return
            if (!this.motif.trim()) { this.erreur_annulation = 'Indiquez le motif de l\'annulation.'; return }
            this.is_saving = true
            this.erreur_annulation = ''
            try {
                const response = await this.changer_statut(this.annulation, 'annule', this.motif.trim())
                if (this.est_detruit) return
                this.is_saving = false
                this.annulation = null
                this.info = response.message || 'Rendez-vous annulé.'
                await this.charger(true)
            } catch (error) {
                if (this.est_detruit) return
                this.is_saving = false
                this.erreur_annulation = message_serveur(error, 'L\'annulation n\'a pas été enregistrée. Actualisez puis réessayez.')
            }
        },
        ouvrir_dossier(ligne) {
            if (!ligne.patient_id) return
            return this.$router.push({ path: '/patients/' + ligne.patient_id + '/historique' })
        },
        format_date(value, complete) {
            const date = calendar.parts(value, this.fuseau_horaire)
            if (!date) return '—'
            return date.day + '/' + date.month + (complete ? '/' + date.year : '') + ' ' + date.hour + ':' + date.minute
        },
        details(ligne) {
            return [
                ['Patient', ligne.patient_nom], ['N° dossier', ligne.dossier], ['Téléphone', ligne.telephone], ['Médecin', ligne.medecin_nom],
                ['Spécialité', ligne.type_consultation],
                // Date complète ; heure fixée ensuite par l'Accueil quand le médecin l'avait laissée vide
                ['Date du rendez-vous', this.date_complete(ligne) + (!this.a_fixer(ligne) && ligne.heure_fixee_par_nom ? ' (heure fixée par ' + ligne.heure_fixee_par_nom + (ligne.heure_fixee_at ? ' le ' + this.format_date(ligne.heure_fixee_at, true) : '') + ')' : '')],
                ['Motif', ligne.motif], ['Commentaire', ligne.commentaire],
                ['Donné', (ligne.source === 'accueil' ? 'à l\'accueil' : 'en consultation') + (ligne.cree_par_nom ? ' par ' + ligne.cree_par_nom : '') + (ligne.created_at ? ' le ' + this.format_date(ligne.created_at, true) : '')],
                ['Consultation d\'origine', ligne.consultation_id ? 'n° ' + ligne.consultation_id : ''],
                ['Rappel', ligne.rappel_libelle + (ligne.dernier_rappel_at ? ' (' + this.format_date(ligne.dernier_rappel_at, true) + ')' : '')],
                ['État', ligne.statut_libelle + (ligne.statut_par_nom ? ' par ' + ligne.statut_par_nom : '') + (ligne.statut_at ? ' le ' + this.format_date(ligne.statut_at, true) : '')],
                ['Motif d\'annulation', ligne.annulation_motif],
                // RDV-005 : arrivée mesurée à la validation du bon en caisse (heure de l'établissement) et consultation du jour
                ['Arrivée en caisse', ligne.arrivee_locale ? String(ligne.arrivee_locale).substr(11, 5) + (Number(ligne.retard_minutes) > 0 ? ' (+' + ligne.retard_minutes + ' min)' : '') : ''],
                ['Consultation du jour', ligne.consultation_honoree_id ? 'n° ' + ligne.consultation_honoree_id : '']
            ]
        },
        // Les métriques ne doivent jamais conserver les chiffres d'une autre période.
        async charger_indicateurs(force = false) {
            if (this.est_detruit || this.mode_infirmier || this.mode_sage_femme) return
            const bornes = this.bornes.slice()
            const periode = this.filtre.periode + '|' + bornes.join('|')
            if (!force && this.indicateurs && this.indicateurs_periode === periode) return
            this.indicateurs = null
            this.indicateurs_periode = ''
            const sequence = ++this.indicateurs_sequence
            try {
                const credential = await (this.tableau_api || store_consultation).getTableauMedecinAccess()
                if (this.est_detruit || sequence !== this.indicateurs_sequence) return
                if (!credential || !credential.success || !credential.data || !credential.data.access_token) throw new Error('Accès indisponible')
                const payload = { access_token: credential.data.access_token, du: bornes[0], au: bornes[1] }
                const response = await (this.tableau_api || store_consultation).getIndicateursRendezVousMedecin(payload)
                if (this.est_detruit || sequence !== this.indicateurs_sequence) return
                if (!response || response.success !== true || !response.data || !response.data.counts) throw new Error('Réponse inattendue')
                this.indicateurs = response.data
                this.indicateurs_periode = periode
            } catch (error) {
                if (!this.est_detruit && sequence === this.indicateurs_sequence) {
                    this.indicateurs = null
                    this.indicateurs_periode = ''
                }
            }
        },
        indicateurs_resume() {
            const i = this.indicateurs
            if (!i || !i.counts) return []
            const pct = v => (v === null || v === undefined) ? '—' : v + ' %'
            const ton = (v, bon, attention, croissant) => {
                if (v === null || v === undefined) return 'muted'
                if (croissant) return v >= bon ? 'success' : (v >= attention ? 'warning' : 'danger')
                return v <= bon ? 'success' : (v <= attention ? 'warning' : 'danger')
            }
            const ponctualite = i.ponctualite || {}
            const rappel = i.rappel || {}
            return [
                { id: 'total', libelle: 'Rendez-vous', valeur: String(i.total), tone: 'primary', aide: i.counts.honore + ' honoré(s), ' + i.counts.absent + ' absent(s), ' + i.counts.annule + ' annulé(s), ' + i.counts.planifie + ' planifié(s)' },
                { id: 'absence', libelle: 'Absence', valeur: pct(i.taux_absence), tone: ton(i.taux_absence, 10, 20, false), aide: 'Absents parmi les rendez-vous renseignés' },
                { id: 'ponctualite', libelle: 'À l\'heure', valeur: pct(ponctualite.a_l_heure), tone: ton(ponctualite.a_l_heure, 80, 60, true),
                    aide: ponctualite.mesures ? 'Arrivée en caisse au plus ' + ponctualite.tolerance_minutes + ' min après l\'heure ; retard moyen ' + ponctualite.retard_moyen_minutes + ' min' : 'Aucune arrivée mesurée en caisse' },
                { id: 'rappel', libelle: 'Rappelés', valeur: pct(rappel.part), tone: ton(rappel.part, 80, 50, true),
                    aide: 'Absence ' + pct(rappel.absence_rappeles) + ' si rappelé, ' + pct(rappel.absence_non_rappeles) + ' sinon' }
            ]
        }
    }
}
